import React, { useState, useEffect, useRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { cpp } from "@codemirror/lang-cpp";
import { java } from "@codemirror/lang-java";
import { abcdef } from "@uiw/codemirror-theme-abcdef";
import { githubLight } from "@uiw/codemirror-theme-github";
import { useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';
import "./iCode.css";
import EnergyVisualizer from "./ER";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import Modal from "./Cnf";
import screenRecordingManager from "../Questions/ScreenRecordingManager";
import { isDesktopApp, stopDesktopProctoring, logCheatingEvent, submitProctoringData, logClipboardEvent, uploadRecording } from "../../utils/desktopBridge";
import { ensureEntireScreenSelected, ENTIRE_SCREEN_REQUIRED_MESSAGE } from "../../utils/screenShare";
import DesktopViolationWarning from "../../components/DesktopViolationWarning";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { 
  useSpecialKeyWarning, 
  useRightClickWarning, 
  usePasteWarning, 
  useTabSwitchWarning 
} from "@/components/ProctoringWarnings";
import { Clock, AlertTriangle, Mic, Eye, CheckCircle, X, Copy, Play, Bug, ChevronDown, ChevronUp, GripVertical, Sun, Moon } from "lucide-react";
// Layer 3 — Real-Time MediaPipe Proctoring. Mounts as a sibling component
// inside the coding screen; reads the existing webcam MediaStream and runs
// detectors per the super-admin feature flags. Adding this is a single
// JSX line below — the engine handles flag-checking, model loading,
// event POSTing, and the warning overlay internally.
import ProctoringEngine from "./Proctoring/ProctoringEngine";

// Layer 0 — Identity Gate. Pre-test wizard that uploads candidate ID,
// captures a live selfie, runs AWS Rekognition CompareFaces, and BLOCKS
// the test until verdict == 'passed' (or 'skipped' if none of the
// identity sub-features are enabled by the super-admin).
import IdentityGate from "./IdentityGate/IdentityGate";

// Layer 2 — Browser Lockdown polish. Three new detectors orchestrated as
// a separate engine: dev-tools detection, virtual-camera detection, and
// multi-monitor detection. The pre-existing lockdown detectors (paste,
// right-click, fullscreen, tab-switch) live in iCode.jsx itself — this
// engine only adds the NEW ones. Renders nothing visible.
import LockdownEngine from "./Lockdown/LockdownEngine";

// Layer 1 — Environment Baseline. 360° room scan that runs AFTER identity
// verification but BEFORE the assessment-instructions modal. Captures
// frames, sends them to Rekognition DetectLabels, builds a per-test
// object whitelist that Layer 4 (Phase 6) will use to differentiate
// "always-present" objects from "appeared mid-test" objects. Auto-skips
// when all Layer 1 feature flags are off.
import EnvironmentBaselineGate from "./EnvironmentBaseline/EnvironmentBaselineGate";

// Layer 4 — Rekognition Snapshots. Server-side periodic + event-triggered
// webcam frame analysis. Compares object detection against the Layer 1
// baseline whitelist (new = high severity, known = low). Runs alongside
// Layer 3 — both share the same webcam stream. Renders an offscreen
// <video> only.
import SnapshotEngine from "./Snapshots/SnapshotEngine";

// Layer ⋆ — Post-coding-question voice follow-up call. When the super-admin
// has the followup_calls_enabled flag on, this modal launches after the
// candidate submits each coding question and runs a short OpenAI Realtime
// voice interview about THEIR code. The candidate cannot proceed to the
// next question until the modal completes (or skips itself if the flag
// flipped off mid-test). See FollowupCallModal.jsx for the contract.
import FollowupCallModal from "./FollowupCallModal";

const CodeEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let { questions, resumeQuestions, testType, backendLanguage, isVeryHard, codingQuestions, screenSharingActive: initialScreenSharingActive } = location.state || {}; // Destructure questions from state
  // Ensure language is always initialized to a valid value
  const [language, setLanguage] = useState(() => {
    const validLanguages = ["Python", "JavaScript", "C++", "C", "Java", "NodeJS"];
    const initialLang = backendLanguage || "Python";
    // Ensure the initial language is valid
    return validLanguages.includes(initialLang) ? initialLang : "Python";
  });
  const [code, setCode] = useState("");
  const [output, setOutput] = useState("");
  const [testResults, setTestResults] = useState([]);
  const [testSummary, setTestSummary] = useState({ passed: 0, total: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = useRef(null);
  const videoStreamRef = useRef(null); // Use a ref to store the video stream
  const [showPopup, setShowPopup] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Set true by the startTest watchdog when the voice intro can't complete
  // within the hard ceiling. Drives a small banner that tells the candidate
  // audio is unavailable but the test is fully usable from on-screen text.
  const [voiceFallbackActive, setVoiceFallbackActive] = useState(false);
  const interviewActiveRef = useRef(true);
  const getQuestionTime = (index) => {
    const q = questions?.[index];
    const minTime = (q?.difficulty || "").toLowerCase() === "hard" ? 20 : 10;
    return Math.max(q?.time_minutes || minTime, minTime) * 60;
  };
  const [timeLeft, setTimeLeft] = useState(() => getQuestionTime(0));
  const minuteAlertSpokenRef   = useRef(false); // To ensure the one-minute alert is spoken only once
  const isTimerExpiredRef      = useRef(false);  // True when auto-submit triggered by timer expiry
  const handleTaskSubmitRef    = useRef(null);   // Always points to latest handleTaskSubmit (avoids stale closure)
  const codeRef                = useRef("");     // Always has latest editor code (avoids stale closure in timer)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userResponses, setUserResponses] = useState(Array((questions?.length || 0)).fill(""));
  const [currentGroup, setCurrentGroup] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [completedLevels, setCompletedLevels] = useState({});
  const [groupQuestions, setGroupQuestions] = useState([]);
  const [currentFlatIndex, setCurrentFlatIndex] = useState(0);
  const [pasteAttempts, setPasteAttempts] = useState(0);
  const [showPasteWarning, setShowPasteWarning] = useState(false);
  const editorRef = useRef(null);

  // ── Post-coding-question follow-up call (anti-cheat) ────────────────────────
  // followupEnabledRef is a REF (not state) so the closure inside
  // handleTaskSubmit always reads the freshest value without the function
  // re-rendering everything every poll. The flag is fetched ONCE on mount.
  // When the modal is opened, we stash the {questionIdx, questionText, code,
  // next} so the modal can render and so its onComplete can fire the deferred
  // advance action. Nothing here renders until followupModalOpen flips true.
  const followupEnabledRef = useRef(false);
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [followupContext,   setFollowupContext]   = useState(null);
  // Pending "advance to next question" callback. Held in a ref because it's
  // a function and React will call it if put in state directly. Wrapped in
  // setNext( () => fn ) when assigning so React doesn't run it immediately.
  const followupAdvanceRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const screenChunksRef = useRef([]);
  const [isScreenRecording, setIsScreenRecording] = useState(Boolean(initialScreenSharingActive));
  const [screenSharingActive, setScreenSharingActive] = useState(Boolean(initialScreenSharingActive));
  const [screenShareError, setScreenShareError] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showFullScreenPrompt, setShowFullScreenPrompt] = useState(true);
  const [violationCount, setViolationCount] = useState(0);
  const [showViolationWarning, setShowViolationWarning] = useState(false);
  const [isTabActive, setIsTabActive] = useState(true);
  const [switchCount, setSwitchCount] = useState(0);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [showTabSwitchWarning, setShowTabSwitchWarning] = useState(false);
  const [exitTimer, setExitTimer] = useState(30);
  const exitTimerRef = useRef(null);
  const [showFullscreenWarning, setShowFullscreenWarning] = useState(false);
  const [testStarted, setTestStarted] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const isRunningRef = useRef(false);

  const [showSpecialKeyWarning, setShowSpecialKeyWarning] = useState(false);
  const [specialKeyAttempts, setSpecialKeyAttempts] = useState(0);
  const [showRightClickWarning, setShowRightClickWarning] = useState(false);
  const [rightClickAttempts, setRightClickAttempts] = useState(0);
  const readyToRunRef = useRef(false);

  // ── Violation counter refs ──────────────────────────────────────────────
  // These mirror the corresponding useState counters. We use refs as the
  // source of truth for "what's the current count right now" because React
  // state updates are async — two violations firing in the same tick
  // (e.g. paste #3 + tab-switch #3) would both read the same stale closure
  // value and under-count. Refs increment synchronously, then we sync state
  // for the UI. Pair every state setter with a ref bump.
  const pasteAttemptsRef       = useRef(0);
  const rightClickAttemptsRef  = useRef(0);
  const specialKeyAttemptsRef  = useRef(0);
  const switchCountRef         = useRef(0);
  const fullscreenExitCountRef = useRef(0);

  // Re-entry guard for forceZeroMarksSubmission(). Without this, two
  // simultaneous violations (paste #3 + tab-switch #3 in the same tick)
  // would each fire the auto-submit, causing duplicate API calls and
  // duplicate screen-recording merges. Once true, stays true forever.
  const forcedZeroSubmittedRef = useRef(false);

  // NEW: show start modal to display conditions before test begins
  const [showStartModal, setShowStartModal] = useState(true);

  // Layer 0 Identity Gate result. Starts FALSE — the gate component
  // resolves it to TRUE either immediately (no identity features enabled
  // = 'skipped' verdict) or after the candidate completes ID upload +
  // selfie + Rekognition face match. The Assessment-Instructions modal
  // and everything downstream is rendered ONLY when identityVerified.
  const [identityVerified, setIdentityVerified] = useState(false);

  // Layer 1 Environment Baseline result. Same on/off pattern as identity
  // — auto-passes when no Layer 1 sub-features are enabled. Renders the
  // baseline gate ONLY after identity has passed; the test-start modal
  // appears only after BOTH gates clear.
  const [environmentBaselineDone, setEnvironmentBaselineDone] = useState(false);
  
  // Test panel for triggering UI elements
  const [showTestPanel, setShowTestPanel] = useState(false);
  
  // Resizable panel state - default to 30% (reduced from 35%)
  const [leftPanelWidth, setLeftPanelWidth] = useState(30);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  const IS_DESKTOP = isDesktopApp();

  const currentQuestionIndex = isVeryHard 
  ? currentGroup * 4 + currentLevel 
  : currentFlatIndex;

  // Extract test_id from URL
  const searchParams = new URLSearchParams(location.search);
  const test_id = searchParams.get("test_id"); // Extract the test_id value from URL

  // New refs for handling audio cancellation
  const currentAudioRef = useRef(null);
  const currentSpeechTokenRef = useRef(0);

  // Add these state variables
  const [typingMetrics, setTypingMetrics] = useState({
    keystrokes: [],
    totalChars: 0,
    pauses: [],
    avgSpeed: 0,
    suspiciousBursts: 0
  });

  const [executionHistory, setExecutionHistory] = useState({
    runs: 0,
    all_tests_passed: false,
    time_taken: 0,
    start_time: Date.now()
  });

  const [audioContext, setAudioContext] = useState(null);
  
  // Full page theme toggle (dark/light) - defaults to light
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Apply dark class to root element when dark mode is enabled
  useEffect(() => {
    const rootElement = document.documentElement;
    if (isDarkMode) {
      rootElement.classList.add('dark');
    } else {
      rootElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // ── Read the followup-calls flag ONCE on mount ──────────────────────────
  // Cheap public read (~50 ms). Stored in a ref so handleTaskSubmit's
  // closure always sees the current value without forcing a re-render
  // every time the AI ticks the timer. A failed fetch leaves the ref at
  // its default of `false` — degrades to the legacy flow (no follow-up
  // call) which is the safer choice if the backend is unavailable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/settings/followup-calls", { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        followupEnabledRef.current = Boolean(data?.enabled);
        // Surface in console for easy debugging — recruiters often ask
        // "is the feature on?" and this is the answer right in the
        // browser devtools without checking the admin panel.
        console.log("[followup] flag enabled =", followupEnabledRef.current);
      } catch (e) {
        console.warn("[followup] flag fetch failed (defaults to off):", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Fast client-side "is this just boilerplate?" prefilter ──────────────
  // Conservative version of services/code_intelligence/attempt_detector.py:
  // strips whitespace + comments and bails out only on OBVIOUSLY-unmodified
  // boilerplate (identical normalised forms OR < 8 substantive characters
  // added). Backend runs the full multi-signal detector — this is just to
  // skip the visible modal flash for the easy-case "candidate skipped Q
  // by hitting Submit without typing anything" path.
  const _isObviouslyBoilerplate = (submitted, boilerplate) => {
    if (!submitted || !submitted.trim()) return true;
    if (!boilerplate) return false;
    const norm = (s) => String(s)
      .replace(/#.*$|\/\/.*$|\/\*[\s\S]*?\*\//gm, "")  // strip comments
      .replace(/\s+/g, "")                              // strip ALL whitespace
      .trim();
    const a = norm(submitted);
    const b = norm(boilerplate);
    if (a === b) return true;
    if (a.length - b.length < 8) return true;  // < ~8 substantive chars added
    return false;
  };

  // ── Helper that intercepts the "advance to next question" step ──────────
  // Every call site that wants to move forward calls THIS instead of
  // directly mutating currentFlatIndex. When the feature is off, the
  // callback runs immediately (legacy behaviour preserved). When on, the
  // FollowupCallModal opens with the snapshot of the just-submitted code;
  // the callback is held in a ref and invoked from onComplete.
  const gateAdvanceWithFollowup = (submittedQuestionIdx, submittedCode, advanceFn) => {
    if (!followupEnabledRef.current || !advanceFn) {
      // No follow-up — run the original advance immediately.
      if (typeof advanceFn === "function") advanceFn();
      return;
    }
    const q = questions?.[submittedQuestionIdx];
    const codeNow = submittedCode || codeRef.current || "";
    const boilerplateForLang = q?.boilerplate?.[language] || "";

    // Skip the modal entirely when the candidate clearly didn't write
    // anything beyond the boilerplate stub. Backend has the
    // authoritative attempt_detector (same threshold) — this is just to
    // avoid the visible modal flash + needless API call.
    if (_isObviouslyBoilerplate(codeNow, boilerplateForLang)) {
      console.log(
        "[followup] q%d submitted as boilerplate-only — skipping modal (client prefilter)",
        submittedQuestionIdx + 1,
      );
      if (typeof advanceFn === "function") advanceFn();
      return;
    }

    setFollowupContext({
      code:           codeNow,
      questionText:   q?.description || "",
      // 0-indexed — MUST match code_fingerprints.question_idx (written
      // 0-indexed via enumerate() in /api/submit_code), because the follow-up
      // is persisted onto that same row. Display code adds +1 where shown.
      questionIdx:    submittedQuestionIdx,
      totalQuestions: questions?.length || 1,
      // test_id comes from the URL query string — the same value the rest
      // of iCode.jsx uses for /api/submit_code, /api/run_code, screen
      // recording uploads, etc. (line 216: `searchParams.get("test_id")`).
      // The earlier draft pulled from location.state which doesn't carry
      // test_id, resulting in test_id="" → backend skipped the session.
      testId:         test_id || "",
      language:       language,
      // Threaded through so the backend's was_attempted() check has
      // something to compare against. Without this, the backend gate
      // would have to look the boilerplate up from the test definition
      // — fine in theory but more moving parts to maintain.
      originalBoilerplate: boilerplateForLang,
    });
    followupAdvanceRef.current = advanceFn;
    setFollowupModalOpen(true);
  };

  useEffect(() => {
    const setupAudioMonitoring = async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const context = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = context.createAnalyser();
      const microphone = context.createMediaStreamSource(stream);
      
      microphone.connect(analyser);
      analyser.fftSize = 256;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const checkAudio = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        
        // Detect sustained talking (possible external help)
        if (average > 50) {  // Adjust threshold as needed
          console.log("Sustained audio detected");
          // Log this event
        }
        
        requestAnimationFrame(checkAudio);
      };
      
      checkAudio();
    };
    
    setupAudioMonitoring();
  }, []);

  // Track keystroke timing
  useEffect(() => {
    let lastKeystroke = Date.now();
    
    const trackTyping = (e) => {
      const now = Date.now();
      const timeSinceLastKey = now - lastKeystroke;
      
      setTypingMetrics(prev => {
        const newKeystrokes = [...prev.keystrokes, { time: now, key: e.key, delay: timeSinceLastKey }];
        const newTotalChars = prev.totalChars + 1;
        
        // Detect suspicious patterns
        let suspiciousBursts = prev.suspiciousBursts;
        
        // Flag: More than 100 characters typed in under 2 seconds (possible paste)
        // Raised from 50 → 100 to avoid false positives for fast typists
        const recentKeys = newKeystrokes.filter(k => now - k.time < 2000);
        if (recentKeys.length > 100) {
          suspiciousBursts++;
          speak("Unusual typing speed detected. Please type naturally.");
        }

        // Flag: Long pause (>60s) followed by large burst of code
        // Raised pause from 30s → 60s, burst from 20 → 50 chars
        if (timeSinceLastKey > 60000 && recentKeys.length > 50) {
          suspiciousBursts++;
          speak("Suspicious activity detected after pause.");
        }
        
        return {
          keystrokes: newKeystrokes.slice(-100), // Keep last 100 keystrokes
          totalChars: newTotalChars,
          pauses: timeSinceLastKey > 5000 ? [...prev.pauses, timeSinceLastKey] : prev.pauses,
          avgSpeed: newTotalChars / ((now - newKeystrokes[0].time) / 1000 / 60), // WPM
          suspiciousBursts
        };
      });
      
      lastKeystroke = now;
    };
    
    document.addEventListener('keydown', trackTyping);
    return () => document.removeEventListener('keydown', trackTyping);
  }, []);

  if (!isVeryHard && questions[0]) {
    questions = questions[0];
  }

  // Check if we're in full-screen mode
  const checkFullScreen = () => {
    const fullscreenElement = document.fullscreenElement || 
                              document.webkitFullscreenElement ||
                              document.mozFullScreenElement ||
                              document.msFullscreenElement;
    setIsFullScreen(!!fullscreenElement);
    
    if (!fullscreenElement && screenSharingActive) {
      // User exited full-screen mode during the test
      handleViolation("fullscreen");
    }
  };

  // Handle various types of violations
  const handleViolation = (type) => {
    // Log to desktop proctoring if running in desktop app
    if (IS_DESKTOP) {
      logCheatingEvent(type, 'medium', {
        message: `${type === 'fullscreen' ? 'Exited fullscreen' : 'Tab switch detected'}`,
        timestamp: new Date().toISOString()
      });
    }

    if (type === "fullscreen") {
      // Ref-based increment to avoid stale-closure under-count when two
      // exits fire in the same React tick. Ref is the source of truth;
      // state is mirrored for the UI.
      fullscreenExitCountRef.current += 1;
      const newCount = fullscreenExitCountRef.current;
      setFullscreenExitCount(newCount);

      if (newCount >= 3) {
        forceZeroMarksSubmission();
        return;
      }

      setShowFullscreenWarning(true);

      // Start countdown timer
      let timeLeft = 30;
      setExitTimer(timeLeft);

      // Clear any existing interval
      if (exitTimerRef.current) {
        clearInterval(exitTimerRef.current);
      }

      exitTimerRef.current = setInterval(() => {
        timeLeft--;
        setExitTimer(timeLeft);

        if (timeLeft <= 0) {
          clearInterval(exitTimerRef.current);
          handleSubmitTest();
        }
      }, 1000);
    } else if (type === "tab_switch") {
      // Ref-based increment (see fullscreen handler above for rationale).
      switchCountRef.current += 1;
      const newCount = switchCountRef.current;
      setSwitchCount(newCount);

      if (newCount >= 3) {
        forceZeroMarksSubmission();
        return;
      }

      setShowTabSwitchWarning(true);
      setTimeout(() => setShowTabSwitchWarning(false), 3000);
    }
  };

  const forceZeroMarksSubmission = async () => {
    // Re-entry guard: prevent duplicate submissions when two violations
    // cross their thresholds in the same tick (or when the user spams a
    // forbidden key very fast). First caller wins; subsequent calls no-op.
    if (forcedZeroSubmittedRef.current) {
      console.log('[Cheating] forceZeroMarksSubmission already in progress, skipping duplicate call');
      return;
    }
    forcedZeroSubmittedRef.current = true;

    await speak("This assessment has been concluded due to proctoring policy violations.");

    interviewActiveRef.current = false;

    if (exitTimerRef.current) clearInterval(exitTimerRef.current);

    await stopScreenRecording();

    // Stop desktop proctoring and submit data
    if (IS_DESKTOP) {
      console.log("[Desktop] Force stopping proctoring due to violations...");
      try {
        const proctoringResult = await stopDesktopProctoring();
        console.log("[Desktop] Proctoring stopped:", proctoringResult);
        await submitProctoringData(test_id, testType);

        // Upload screen recording to S3
        console.log("[Desktop] Uploading screen recording...");
        const uploadResult = await uploadRecording(test_id);
        console.log("[Desktop] Recording upload result:", uploadResult);
      } catch (error) {
        console.error("[Desktop] Error stopping proctoring:", error);
      }
    }

    const updatedResponses = [...userResponses];
    updatedResponses[currentQuestionIndex] = code;
    setUserResponses(updatedResponses);

    fetch("/api/submit_code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        test_id: test_id,
        test_type: testType,
        questions,
        user_responses: updatedResponses,
        language,
        difficulty: isVeryHard ? "Very Hard" : "Medium",
        score: 0,
        violations: {
          pasteAttempts,
          specialKeyAttempts,
          rightClickAttempts,
          switchCount,
          fullscreenExitCount
        },
        typing_metrics: {
          avgSpeed: typingMetrics.avgSpeed,
          suspiciousBursts: typingMetrics.suspiciousBursts,
          longPauses: typingMetrics.pauses.filter(p => p > 30000).length,
          totalKeystrokes: typingMetrics.totalChars
        },
        execution_history: {
          runs: executionHistory.runs,
          all_tests_passed: executionHistory.all_tests_passed,
          time_taken: executionHistory.time_taken
        },
        forced_submission: true
      }),
    });

    fetch('/api/merge_screen_recording', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ test_id })
    });

    setShowPopup(true);

    setTimeout(() => {
      window.location.href = "/user-home";
    }, 3000);
  };

  useEffect(() => {
    setExecutionHistory({
      runs: 0,
      all_tests_passed: false,
      time_taken: 0,
      start_time: Date.now()
    });
  }, [currentQuestionIndex]);

  // Monitor visibility changes (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is not active
        setIsTabActive(false);
        handleViolation("tab_switch");
      } else {
        setIsTabActive(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [switchCount, fullscreenExitCount]);

  // Monitor full-screen changes
  useEffect(() => {
    const fullscreenChangeEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];
    
    fullscreenChangeEvents.forEach(event => {
      document.addEventListener(event, checkFullScreen);
    });
    
    return () => {
      fullscreenChangeEvents.forEach(event => {
        document.removeEventListener(event, checkFullScreen);
      });
    };
  }, [screenSharingActive]);

  // Return to fullscreen after warning
  const handleReturnToFullscreen = () => {
    setShowFullscreenWarning(false);
    const elem = document.documentElement;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
    
    // Clear the exit timer
    if (exitTimerRef.current) {
      clearInterval(exitTimerRef.current);
    }
  };

  // Function to handle paste attempts
  const handlePasteAttempt = () => {
    // Ref-based increment — see violation counter ref block at the top of
    // the component for the rationale. Without this, two paste events in
    // the same React tick (browser fires copy + paste in succession on
    // some shortcuts) under-count and the candidate gets fewer strikes
    // than they should.
    pasteAttemptsRef.current += 1;
    const newAttempts = pasteAttemptsRef.current;
    setPasteAttempts(newAttempts);
    setShowPasteWarning(true);

    if(!isSpeaking){ 
      speak("Copy-paste is not allowed in this assessment. Multiple attempts may result in disqualification.");
    }
    
    // Speak warning on 3rd attempt
    if (newAttempts >= 3) {
      forceZeroMarksSubmission();
    }
    
    // Hide warning after 3 seconds
    setTimeout(() => {
      setShowPasteWarning(false);
    }, 3000);
  };

  // COMPREHENSIVE INPUT RESTRICTIONS - Add this useEffect
  useEffect(() => {
    // Function to disable right-click context menu
    const disableContextMenu = (e) => {
      e.preventDefault();
      // Ref-based increment (see violation counter ref block at top).
      rightClickAttemptsRef.current += 1;
      const newAttempts = rightClickAttemptsRef.current;
      setRightClickAttempts(newAttempts);
      setShowRightClickWarning(true);

      if(!isSpeaking){
        speak("Right-click is not allowed in this assessment. Multiple attempts may result in disqualification.");
      }
      
      // Speak warning on 3rd attempt
      if (newAttempts >= 3) {
        forceZeroMarksSubmission();
      }
      
      // Hide warning after 3 seconds
      setTimeout(() => {
        setShowRightClickWarning(false);
      }, 5000);
      
      return false;
    };

    // Function to disable special keys (Alt, Windows, F1-F12, etc.)
    const disableSpecialKeys = (e) => {
      const restrictedKeys = {
        // Alt keys
        18: 'Alt', // Left and Right Alt
        // Windows keys
        91: 'Windows Left', 
        92: 'Windows Right',
        93: 'Windows Menu',
      };

      if (restrictedKeys[e.keyCode]) {
        e.preventDefault();
        // Ref-based increment (see violation counter ref block at top).
        specialKeyAttemptsRef.current += 1;
        const newAttempts = specialKeyAttemptsRef.current;
        setSpecialKeyAttempts(newAttempts);
        setShowSpecialKeyWarning(true);

        if(!isSpeaking){
          speak(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);
        }
        
        // Speak warning on 3rd attempt
        if (newAttempts >= 3) {
          forceZeroMarksSubmission();
        }
        
        // Hide warning after 3 seconds
        setTimeout(() => {
          setShowSpecialKeyWarning(false);
        }, 5000);
        
        return false;
      }

      // Detect Ctrl+Alt+Del (though this is often handled at OS level)
      if (e.ctrlKey && e.altKey && e.keyCode === 46) { // Delete key
        e.preventDefault();
        // Ref-based increment (see violation counter ref block at top).
        specialKeyAttemptsRef.current += 1;
        const newAttempts = specialKeyAttemptsRef.current;
        setSpecialKeyAttempts(newAttempts);
        setShowSpecialKeyWarning(true);

        if(!isSpeaking){
          speak(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);
        }
        
        // Speak warning on 3rd attempt
        if (newAttempts >= 3) {
          forceZeroMarksSubmission();
        }

        setTimeout(() => setShowSpecialKeyWarning(false), 5000);
        return false;
      }

      // Detect Alt+Tab (tab switching)
      if (e.altKey && e.keyCode === 9) {
        e.preventDefault();
        // Ref-based increment (see violation counter ref block at top).
        specialKeyAttemptsRef.current += 1;
        const newAttempts = specialKeyAttemptsRef.current;
        setSpecialKeyAttempts(newAttempts);
        setShowSpecialKeyWarning(true);

        if(!isSpeaking){
          speak(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);
        }
        
        // Speak warning on 3rd attempt
        if (newAttempts >= 3) {
          forceZeroMarksSubmission();
        }

        setTimeout(() => setShowSpecialKeyWarning(false), 5000);
        return false;
      }

      // Detect Alt+F4 (window close)
      if (e.altKey && e.keyCode === 115) { // F4
        e.preventDefault();
        // Ref-based increment (see violation counter ref block at top).
        specialKeyAttemptsRef.current += 1;
        const newAttempts = specialKeyAttemptsRef.current;
        setSpecialKeyAttempts(newAttempts);
        setShowSpecialKeyWarning(true);

        if(!isSpeaking){
          speak(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);
        }
        
        // Speak warning on 3rd attempt
        if (newAttempts >= 3) {
          forceZeroMarksSubmission();
        }

        setTimeout(() => setShowSpecialKeyWarning(false), 5000);
        return false;
      }
    };

    // Function to disable keyboard shortcuts for copy/paste
    const disableShortcuts = (e) => {
      // Detect Ctrl+C, Ctrl+V, Ctrl+X, Cmd+C, Cmd+V, Cmd+X
      if ((e.ctrlKey || e.metaKey) && 
          (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'C' || e.key === 'V' || e.key === 'X')) {
        e.preventDefault();
        handlePasteAttempt();
        return false;
      }
      
      // Detect right-click context menu key
      if (e.key === 'ContextMenu') {
        e.preventDefault();
        return false;
      }
    };

    // Function to handle paste attempts
    const handlePaste = async (e) => {
      e.preventDefault();

      // Log to desktop app for server-side tracking (can't be bypassed)
      if (IS_DESKTOP) {
        try {
          const pastedText = e.clipboardData?.getData('text') || '';
          await logClipboardEvent('paste', pastedText.length, pastedText.substring(0, 50));
        } catch (err) {
          console.error('[Desktop] Error logging clipboard event:', err);
        }
      }

      handlePasteAttempt();
      return false;
    };

    // Function to handle copy events
    const handleCopy = (e) => {
      e.preventDefault();
      handlePasteAttempt();
      return false;
    };

    // Function to handle cut events
    const handleCut = (e) => {
      e.preventDefault();
      handlePasteAttempt();
      return false;
    };

    // Add all event listeners
    document.addEventListener('contextmenu', disableContextMenu);
    document.addEventListener('keydown', disableSpecialKeys);
    document.addEventListener('keydown', disableShortcuts);
    
    // Add event listeners to the editor specifically
    const editorElement = editorRef.current;
    if (editorElement) {
      editorElement.addEventListener('paste', handlePaste);
      editorElement.addEventListener('copy', handleCopy);
      editorElement.addEventListener('cut', handleCut);
      editorElement.addEventListener('contextmenu', disableContextMenu);
    }

    // Also prevent drag-and-drop of text. Named handlers so the cleanup
    // function can actually remove them — the original code passed fresh
    // anonymous functions to removeEventListener which never matched the
    // ones registered, so these listeners piled up on every re-render.
    const handleDragStart = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        e.preventDefault();
      }
    };
    const handleDrop = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        e.preventDefault();
        handlePasteAttempt();
      }
    };
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('drop', handleDrop);

    // Clean up all event listeners. Uses the SAME function references that
    // were registered above — anonymous arrow functions in removeEventListener
    // are silently ignored by the browser because they aren't the same
    // object as the ones registered.
    return () => {
      document.removeEventListener('contextmenu', disableContextMenu);
      document.removeEventListener('keydown', disableSpecialKeys);
      document.removeEventListener('keydown', disableShortcuts);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('drop', handleDrop);

      if (editorElement) {
        editorElement.removeEventListener('paste', handlePaste);
        editorElement.removeEventListener('copy', handleCopy);
        editorElement.removeEventListener('cut', handleCut);
        editorElement.removeEventListener('contextmenu', disableContextMenu);
      }
    };
  }, []);  // Empty deps: handlers read counts from refs, not from closures.
            // Avoids tearing down/re-registering all listeners on every
            // violation, which previously caused churn + the leak above.

  // Monitor for serious violations that trigger auto-submission
  useEffect(() => {
    const totalViolations = pasteAttempts + specialKeyAttempts + rightClickAttempts + switchCount;
    
    if (totalViolations >= 10) { // Adjust threshold as needed
      forceZeroMarksSubmission();
    }
  }, [pasteAttempts, specialKeyAttempts, rightClickAttempts, switchCount]);

  // Fallback speech function using browser's speech synthesis
  async function speak_def(textToSpeak) {
    if (!textToSpeak || textToSpeak.trim() === '') {
      console.log('[Voice] speak_def: Empty text, skipping');
      return;
    }

    console.log('[Voice] speak_def: Using browser speech synthesis for:', textToSpeak.substring(0, 50) + '...');

    return new Promise((resolve) => {
      if ('speechSynthesis' in window) {
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;

        // Chrome bug: speechSynthesis.onend sometimes never fires (especially for
        // long texts or when tab is not focused). Safety timeout forces resolve.
        const estimatedMs = Math.max(8000, (textToSpeak.length / 12) * 1000 + 3000);
        const safetyTimeout = setTimeout(() => {
          console.warn('[Voice] speak_def: Safety timeout — forcing resolve after', estimatedMs, 'ms');
          clearInterval(resumeInterval);
          resolve();
        }, estimatedMs);

        // Chrome bug: speechSynthesis silently pauses after ~15s.
        // Periodically resume to keep it alive.
        const resumeInterval = setInterval(() => {
          if (window.speechSynthesis.speaking && window.speechSynthesis.paused) {
            window.speechSynthesis.resume();
          }
        }, 5000);

        utterance.onstart = () => {
          console.log('[Voice] speak_def: Speech started');
        };

        utterance.onend = () => {
          clearTimeout(safetyTimeout);
          clearInterval(resumeInterval);
          console.log('[Voice] speak_def: Speech ended');
          resolve();
        };

        utterance.onerror = (error) => {
          clearTimeout(safetyTimeout);
          clearInterval(resumeInterval);
          console.error('[Voice] speak_def: Speech error:', error);
          resolve();
        };

        window.speechSynthesis.speak(utterance);
      } else {
        console.warn('[Voice] speak_def: Browser speech synthesis not available');
        resolve();
      }
    });
  }

  async function speak(text) {
    console.log('[Voice] speak: Called with text:', text?.substring(0, 100) + (text?.length > 100 ? '...' : ''));
    
    if (!text || text.trim() === '') {
      console.warn('[Voice] speak: Empty text provided');
      return;
    }

    // Increment token to cancel any previous speech
    currentSpeechTokenRef.current++;
    const myToken = currentSpeechTokenRef.current;
    console.log('[Voice] speak: Token set to', myToken);
    
    if (currentAudioRef.current) {
      console.log('[Voice] speak: Cancelling previous audio');
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Cancel any ongoing browser speech synthesis
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      console.log('[Voice] speak: Cancelled browser speech synthesis');
    }

    const sentences = text
      .split(/\. +/)
      .map(sentence => sentence.trim())
      .filter(Boolean)
      .map(sentence => sentence.endsWith('.') ? sentence : sentence + '.');

    console.log('[Voice] speak: Split into', sentences.length, 'sentences');

    if (sentences.length === 0) {
      console.warn('[Voice] speak: No sentences after processing');
      return;
    }

    async function fetchAudio(sentence) {
      console.log('[Voice] fetchAudio: Fetching audio for:', sentence.substring(0, 50) + '...');
      try {
        // Enterprise safeguard: cap the network wait. A TTS server that
        // accepts the connection but never streams the body would otherwise
        // hang this await indefinitely, blocking the whole interview gate.
        // 8s is generous for a single-sentence TTS call; on timeout we
        // return null and the caller falls through to speak_def (browser TTS).
        const response = await axios.post(
          '/api/generate_audio',
          { text: sentence },
          { responseType: 'blob', timeout: 8000 }
        );
        const url = URL.createObjectURL(response.data);
        console.log('[Voice] fetchAudio: Success, created URL:', url.substring(0, 50) + '...');
        return url;
      } catch (error) {
        console.error('[Voice] fetchAudio: Error fetching audio:', error.message || error);
        return null;
      }
    }

    console.log('[Voice] speak: Fetching audio for first sentence');
    let currentAudioUrl = await fetchAudio(sentences[0]);
    console.log('[Voice] speak: First audio URL:', currentAudioUrl ? 'Success' : 'Failed (will use fallback)');

    for (let i = 1; i < sentences.length; i++) {
      // Check cancellation token before continuing
      if (currentSpeechTokenRef.current !== myToken) {
        console.log('[Voice] speak: Cancelled (token mismatch)');
        return;
      }
      
      console.log(`[Voice] speak: Processing sentence ${i + 1}/${sentences.length}`);
      const nextAudioPromise = fetchAudio(sentences[i]);
      setIsSpeaking(true);

      // Two-level fallback for THIS sentence:
      //   1. fetchAudio failed (Polly 5xx, timeout, etc.) → use browser TTS
      //   2. fetchAudio succeeded but playAudio reports failure (autoplay
      //      block, decode error, mid-stream stall) → ALSO use browser TTS
      // Previously case 2 was silently dropped, so a sentence could be
      // skipped entirely and the candidate would miss part of the question.
      //
      // CRITICAL: check token IMMEDIATELY after await playAudio, BEFORE the
      // fallback decision. When the user rapidly submits multiple tasks, an
      // OLD playAudio's watchdog can fire long after its speak() call was
      // superseded — without this guard, we'd trigger browser TTS for the
      // OLD sentence while the NEW question is already playing, causing
      // two voices to overlap.
      if (currentAudioUrl) {
        console.log('[Voice] speak: Playing audio for sentence', i);
        const played = await playAudio(currentAudioUrl, myToken);
        if (currentSpeechTokenRef.current !== myToken) {
          console.log('[Voice] speak: Cancelled after playback (token mismatch) — skipping fallback');
          return;
        }
        if (!played) {
          console.log('[Voice] speak: Polly audio failed to play, falling back to browser TTS for sentence', i);
          await speak_def(sentences[i - 1]);
          if (currentSpeechTokenRef.current !== myToken) {
            console.log('[Voice] speak: Cancelled during fallback (token mismatch)');
            return;
          }
        }
      } else {
        console.log('[Voice] speak: Audio failed, using fallback for sentence', i);
        await speak_def(sentences[i - 1]);
        if (currentSpeechTokenRef.current !== myToken) {
          console.log('[Voice] speak: Cancelled during fallback (token mismatch)');
          return;
        }
      }

      if (currentSpeechTokenRef.current !== myToken) {
        console.log('[Voice] speak: Cancelled during playback (token mismatch)');
        return;
      }

      currentAudioUrl = await nextAudioPromise;
      console.log('[Voice] speak: Next audio URL:', currentAudioUrl ? 'Success' : 'Failed (will use fallback)');
    }

    setIsSpeaking(true);
    console.log('[Voice] speak: Playing final sentence');
    // Same two-level fallback for the final sentence, with the same
    // critical token guards: if a new speak() superseded us during the
    // await, do NOT fall back to browser TTS — otherwise the stale
    // sentence would be read aloud on top of the new question's audio.
    if (currentAudioUrl) {
      console.log('[Voice] speak: Playing final audio');
      const played = await playAudio(currentAudioUrl, myToken);
      if (currentSpeechTokenRef.current !== myToken) {
        console.log('[Voice] speak: Cancelled after final playback (token mismatch) — skipping fallback');
        return;
      }
      if (!played) {
        console.log('[Voice] speak: Polly final audio failed to play, falling back to browser TTS');
        await speak_def(sentences[sentences.length - 1]);
        if (currentSpeechTokenRef.current !== myToken) {
          console.log('[Voice] speak: Cancelled during final fallback (token mismatch)');
          return;
        }
      }
    } else {
      console.log('[Voice] speak: Final audio failed, using fallback');
      await speak_def(sentences[sentences.length - 1]);
      if (currentSpeechTokenRef.current !== myToken) {
        console.log('[Voice] speak: Cancelled during final fallback (token mismatch)');
        return;
      }
    }
    setIsSpeaking(false);
    console.log('[Voice] speak: Completed');
  };

  // playAudio now returns a boolean indicating whether the clip actually played
  // to completion. The caller (`speak`) uses this to decide whether to fall
  // back to browser TTS for the same sentence — closing the last gap in the
  // fallback chain where Polly returns valid audio but the browser fails to
  // decode/play it (autoplay blocked, codec error, mid-stream stall).
  //   - true  → audio played end-to-end (onended OR watchdog with cur≈duration)
  //   - false → definite play failure (onerror, audio.play() rejection,
  //             watchdog firing well before duration)
  function playAudio(url, token) {
    return new Promise((resolve) => {
      // Check if URL is valid before creating Audio object
      if (!url) {
        console.warn("[Voice] playAudio: Cannot play audio: invalid URL");
        resolve(false); // signal failure → speak() will use browser TTS
        return;
      }

      console.log('[Voice] playAudio: Creating audio object, token:', token);
      // Create a new audio instance and store it in the ref
      const audio = new Audio(url);
      currentAudioRef.current = audio;

      let checkInterval;
      let settled = false;
      // Enterprise safeguard: hard ceiling so a hung audio element
      // (stalled mid-stream, codec hang, missing `onended` event — all
      // observed in production) cannot freeze the interview UI. Starts
      // at 45s, tightens to (audioDuration + 5s grace) once metadata
      // loads so a 6s clip can't block for 45s.
      let safetyMs = 45000;
      let safetyTimer = null;

      const safeResolve = (success, reason) => {
        if (settled) return;
        settled = true;
        if (checkInterval) clearInterval(checkInterval);
        if (safetyTimer) clearTimeout(safetyTimer);
        // Clear the ref only if it still belongs to the same token
        if (currentSpeechTokenRef.current === token) {
          currentAudioRef.current = null;
        }
        if (reason) console.warn('[Voice] playAudio: resolved via', reason, 'success=', success);
        resolve(success);
      };
      const armSafety = (ms) => {
        if (safetyTimer) clearTimeout(safetyTimer);
        safetyTimer = setTimeout(() => {
          // When the watchdog fires, distinguish "audio essentially finished
          // but onended never fired" from "audio truly stuck". If currentTime
          // is within ~1s of duration, the clip played fully → success=true
          // (no need to bother the user with a duplicate browser-TTS read).
          // If we stalled well below duration, it's a real failure → fall
          // back to browser TTS.
          const dur = isFinite(audio.duration) ? audio.duration : 0;
          const cur = audio.currentTime || 0;
          const playedMost = dur > 0 && cur >= dur - 1;
          safeResolve(playedMost, `watchdog ${ms}ms (cur=${cur.toFixed(1)}/dur=${dur.toFixed(1)})`);
        }, ms);
      };
      armSafety(safetyMs);

      audio.onloadedmetadata = () => {
        // Tighten the watchdog once we know the real clip length —
        // (duration*1000 + 5s grace) is plenty for normal playback and
        // catches stalls much faster than the 45s default.
        if (isFinite(audio.duration) && audio.duration > 0) {
          const tight = Math.min(safetyMs, Math.ceil(audio.duration * 1000) + 5000);
          armSafety(tight);
          console.log('[Voice] playAudio: tightened watchdog to', tight, 'ms (duration', audio.duration, 's)');
        }
      };

      audio.onloadeddata = () => {
        console.log('[Voice] playAudio: Audio data loaded');
      };

      audio.oncanplay = () => {
        console.log('[Voice] playAudio: Audio can play');
      };

      audio.onplay = () => {
        console.log('[Voice] playAudio: Audio playback started');
      };

      audio.onended = () => {
        console.log('[Voice] playAudio: Audio playback ended');
        safeResolve(true); // clean end-to-end playback
      };

      audio.onerror = (e) => {
        console.error("[Voice] playAudio: Audio playback error:", e, audio.error);
        safeResolve(false, 'audio.onerror'); // definite decode failure
      };

      audio.onpause = () => {
        console.log('[Voice] playAudio: Audio paused');
      };

      console.log('[Voice] playAudio: Attempting to play audio');
      audio.play().then(() => {
        console.log('[Voice] playAudio: Play promise resolved');
      }).catch((error) => {
        console.error("[Voice] playAudio: Error playing audio:", error);
        safeResolve(false, 'audio.play() rejection'); // definite play failure
      });

      checkInterval = setInterval(() => {
        if (!interviewActiveRef.current) {
          console.log('[Voice] playAudio: Interview inactive, pausing audio');
          audio.pause();  // Pause the audio if interview is inactive
        }
        // If a newer speak() has bumped the token, this playAudio is now
        // stale. Resolve it quickly (instead of waiting up to 20s for the
        // watchdog) so the OLD speak() can exit via its token-mismatch
        // guard. Without this, every rapid task-submit leaves a zombie
        // watchdog scheduled to fire 10-20s later.
        if (currentSpeechTokenRef.current !== token && !settled) {
          console.log('[Voice] playAudio: token mismatch detected — resolving stale playAudio');
          audio.pause();
          safeResolve(false, 'token mismatch via checkInterval');
        }
      }, 100);
    });
  };

  const streamRef = useRef(null);
  
  let mediaRecorder;

  async function startStreaming() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() => null);
      if (!stream) {
        console.warn("Failed to get user media");
        return;
      }
      
      streamRef.current = stream;
      
      // Wait a bit for video element to be ready (React Strict Mode)
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              // Autoplay blocked - will try again on user interaction
              console.warn("Video autoplay blocked:", err?.message || err);
            });
          }
        };
        videoRef.current.onerror = (e) => {
          console.error("Video element error:", e);
        };
      }

      try {
        mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
        mediaRecorder.start(1000); // send every second
      } catch (recorderError) {
        console.warn("MediaRecorder not supported or failed:", recorderError);
      }
    } catch (error) {
      console.error("Error starting video stream:", error);
    }
  }

  // 4. Start Test with Welcome and First Question
  //
  // Defense-in-depth around the voice intro: the Run button and the question
  // timer are both gated on `readyToRunRef.current === true`. If the TTS path
  // hangs anywhere (network mid-stream, codec stall, missing `onended`,
  // browser autoplay block with no error event), the candidate is locked out
  // of the test. We wrap the voice flow in a top-level watchdog so that even
  // if every inner timeout is somehow defeated, the gate ALWAYS opens within
  // a bounded time. We also force a re-render after flipping the ref, since
  // refs alone don't trigger React updates.
  const startTest = async () => {
    console.log('[Voice] startTest: Starting test, currentQuestionIndex:', currentQuestionIndex);
    setTestStarted(true);

    // Top-level ceiling. Worst-case legitimate intro+question read is ~45s,
    // so 120s gives generous headroom and still guarantees the candidate is
    // unblocked well before the 30-minute timer matters.
    const VOICE_HARD_CEILING_MS = 120000;
    let voiceFinished = false;

    const voiceFlow = (async () => {
      try {
        // Welcome message
        console.log('[Voice] startTest: Speaking welcome message');
        await speak(
          "Hello and Welcome to your coding interview! There are questions available, and you will be able to attempt them one at a time. Each question will appear after you've completed the previous one. Good luck!"
        );

        // Speak first question
        if (questions && questions[currentQuestionIndex]?.description) {
          console.log('[Voice] startTest: Speaking first question description');
          await speak(`Here is your first question: ${questions[currentQuestionIndex].description}`);
        } else {
          console.warn('[Voice] startTest: No question description found, using fallback message');
          await speak("Please check the problem description on the left side of the screen.");
        }
      } catch (err) {
        console.error('[Voice] startTest: speak error (will still enable Run Code):', err);
      } finally {
        voiceFinished = true;
      }
    })();

    let watchdogFired = false;
    const watchdog = new Promise((resolve) => {
      setTimeout(() => {
        if (!voiceFinished) {
          watchdogFired = true;
          console.warn(
            '[Voice] startTest: HARD CEILING hit at',
            VOICE_HARD_CEILING_MS,
            'ms — forcing Run+timer enable, surfacing UI fallback.'
          );
        }
        resolve();
      }, VOICE_HARD_CEILING_MS);
    });

    // Whichever finishes first unblocks the gate. If the watchdog wins,
    // voiceFlow's promise leaks harmlessly (we no longer await it) and the
    // candidate can start coding. If voiceFlow wins, the watchdog timeout
    // still fires later but `voiceFinished` is true so it's a no-op.
    await Promise.race([voiceFlow, watchdog]);

    // Surface a UI signal so the candidate knows audio failed but the test
    // is fully functional — without this, silent failure is indistinguishable
    // from a frozen page.
    if (watchdogFired) {
      setVoiceFallbackActive(true);
      try {
        // Cancel any in-flight browser TTS / HTML audio so it doesn't start
        // playing 30 seconds late on top of a candidate who's already coding.
        if ('speechSynthesis' in window) window.speechSynthesis.cancel();
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
        }
        currentSpeechTokenRef.current++;
      } catch (_) { /* defensive only */ }
    }

    // Flip the gate AND force a re-render. `readyToRunRef` is a ref, so the
    // Run button's `disabled={!readyToRunRef.current}` won't re-evaluate
    // until some state change schedules a render. `setIsSpeaking(false)`
    // doubles as cleanup (covers the edge case where speak() threw before
    // its own setIsSpeaking(false) at the end of its body).
    readyToRunRef.current = true;
    setIsSpeaking(false);
    console.log('[Voice] startTest: readyToRun set to true (post-race, watchdogFired=', watchdogFired, ')');
  };

  const initializeEverything = async () => {
    console.log('[Voice] initializeEverything: Starting initialization');
    try {
      // Step 1: Start camera immediately (wait a bit for video element to be ready in React Strict Mode)
      await new Promise(resolve => setTimeout(resolve, 150));
      console.log('[Voice] initializeEverything: Starting video stream');
      await startStreaming();
      
      // Step 4: Start the test with welcome message and first question
      console.log('[Voice] initializeEverything: Starting test');
      await startTest();
      console.log('[Voice] initializeEverything: Initialization complete');
    } catch (error) {
      console.error("[Voice] initializeEverything: Initialization failed:", error);
      setScreenShareError("Initialization failed: " + (error?.message || String(error)));
    }
  };

  // NOTE: initialization will now occur AFTER user confirms start modal
  useEffect(() => {
    let mounted = true;
    
    console.log('[Voice] useEffect: showStartModal changed to:', showStartModal);
    
    // If start modal closed (user clicked I understood), initialize
    if (!showStartModal) {
      console.log('[Voice] useEffect: Start modal closed, initializing...');
      initializeEverything();
    } else {
      console.log('[Voice] useEffect: Start modal still open, waiting...');
    }

    // Cleanup on unmount
    return () => {
      console.log('[Voice] useEffect: Cleaning up');
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      if (currentAudioRef.current) {
        console.log('[Voice] useEffect: Pausing audio on cleanup');
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
    };
  }, [showStartModal]);

  // Use shared warning hooks for non-blocking warnings (prevents overlap)
  useSpecialKeyWarning(showSpecialKeyWarning);
  useRightClickWarning(showRightClickWarning);
  usePasteWarning(showPasteWarning);
  useTabSwitchWarning(showTabSwitchWarning, switchCount);

  // Resizable panel handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const container = resizeRef.current;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Constrain between 20% and 60%
      const constrainedWidth = Math.max(20, Math.min(60, newLeftWidth));
      setLeftPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Handle screen recording state when iCode loads
  useEffect(() => {
    // Get screen recording state from global manager
    const recordingState = screenRecordingManager.getScreenRecordingState();
    
    console.log("iCode loaded - Screen recording state:", recordingState);

    // If screen recording is active from TestingSection, restore the state
    if (recordingState.isScreenRecording && recordingState.screenSharingActive) {
      // Restore refs
      screenStreamRef.current = recordingState.screenStreamRef;
      screenRecorderRef.current = recordingState.screenRecorderRef;
      screenChunksRef.current = recordingState.screenChunksRef;
      
      // Restore state
      setIsScreenRecording(recordingState.isScreenRecording);
      setScreenSharingActive(recordingState.screenSharingActive);
      setScreenShareError(recordingState.screenShareError);
      setTestStarted(recordingState.testStarted);
      
      // Request fullscreen when restoring screen recording
      handleReturnToFullscreen();
      
      console.log("Screen recording restored from TestingSection - starting coding test");
    }
  }, []);

  // Initialize group questions
  useEffect(() => {
    if (isVeryHard) {
      // Group questions into sets of 4 (Medium, Hard, Very Hard, Extreme)
      const groups = [];
      for (let i = 0; i < questions.length; i += 4) {
        groups.push(questions.slice(i, i + 4));
      }
      setGroupQuestions(groups);
    }
  }, [isVeryHard, questions]);

  // Ensure language is always set to a valid value
  useEffect(() => {
    const validLanguages = ["Python", "JavaScript", "C++", "C", "Java", "NodeJS"];
    if (!language || !validLanguages.includes(language)) {
      setLanguage("Python");
    }
  }, []);

  useEffect(() => {
    if (userResponses[currentQuestionIndex]) {
      setCode(userResponses[currentQuestionIndex]);
    } else {
      setCode(questions[currentQuestionIndex]?.boilerplate[language] || "");
    }
  }, [language, questions, currentQuestionIndex, userResponses]);

  // Start screen recording with user guidance
  const initializeScreenRecording = async () => {
    setIsButtonDisabled(true); // Disable button to prevent multiple clicks
    try {
      await startScreenRecording();
      startStreaming();
      await speak(
        "Hello and Welcome to your coding interview! There are four questions available, and you will be able to attempt them one at a time. Each question will appear after you've completed the previous one. Good luck!. Here is your first question."
      );
      await speak(questions[currentQuestionIndex]?.description);
    } catch (error) {
      console.error("Failed to initialize screen recording:", error);
    } finally {
      readyToRunRef.current = true;
      console.log('[Voice] initializeScreenRecording: readyToRun set to true (finally block)');
    }
  };

  // Keep ref always pointing to latest handleTaskSubmit so timer never uses stale closure
  useEffect(() => {
    handleTaskSubmitRef.current = handleTaskSubmit;
  });

  useEffect(() => {
    setTestResults([]);
    setOutput("");

    let timerDuration;

    if(isVeryHard) {
      timerDuration = 1800;
    } else {
      timerDuration = getQuestionTime(currentFlatIndex);
    }

    setTimeLeft(timerDuration);
    minuteAlertSpokenRef.current = false;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          isTimerExpiredRef.current = true; // Mark as timer-triggered auto-submit
          // Use ref so we always call the LATEST handleTaskSubmit with fresh state
          handleTaskSubmitRef.current?.();
          return 0;
        }
        // When about to hit 60 seconds remaining, speak the alert.
        if (prevTime === 61 && !minuteAlertSpokenRef.current) {
          speak("Hurry Up! Only one minute left");
          minuteAlertSpokenRef.current = true;
        }
        if (isRunningRef.current || !readyToRunRef.current) {
          return prevTime; // Pause timer while code is running
        }
        return prevTime - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [currentQuestionIndex, currentGroup, isVeryHard, currentFlatIndex]);

  // Cleanup screen recording state when component unmounts
  useEffect(() => {
    return () => {
      // Clear screen recording state when component unmounts
      screenRecordingManager.clearScreenRecordingState();
    };
  }, []);

  // Function to start screen recording
  const startScreenRecording = async () => {
    try {
      // Provide clear instructions to the user first
      speak("For this assessment, we need to record your screen. Please select entire screen option from the pop up.");

      // Request screen capture
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: "screen" }
      });
      ensureEntireScreenSelected(screenStream);
      
      screenStreamRef.current = screenStream;
      
      // Create media recorder
      const recorder = new MediaRecorder(screenStream, {
        mimeType: 'video/webm; codecs=vp9',
        videoBitsPerSecond: 1200000
      });
      
      screenRecorderRef.current = recorder;
      screenChunksRef.current = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          screenChunksRef.current.push(event.data);

          // Send chunk to backend every 5 seconds
          if (screenChunksRef.current.length >= 3) {
            sendScreenChunkToBackend();
          }
        }
      };

      // onstop fires AFTER the final ondataavailable — flush any remaining chunks
      recorder.onstop = async () => {
        if (screenChunksRef.current.length > 0) {
          try {
            await sendScreenChunkToBackend();
          } catch (e) {
            console.error("[iCode] Error flushing final screen chunk on stop:", e);
          }
        }
      };

      // Start recording and capture data every second
      recorder.start(1000);
      setIsScreenRecording(true);
      setScreenSharingActive(true);
      setScreenShareError("");

      handleReturnToFullscreen();

      setTestStarted(true);
      
      // Handle when user stops sharing screen
      screenStream.getVideoTracks()[0].onended = () => {
        stopScreenRecording();
      };
      
    } catch (error) {
      console.error("Error starting screen recording:", error);
      const message = error.code === "ENTIRE_SCREEN_REQUIRED"
        ? ENTIRE_SCREEN_REQUIRED_MESSAGE
        : "Screen sharing permission was not granted. Please allow screen sharing to continue with the assessment.";
      setScreenShareError(message);
      speak(error.code === "ENTIRE_SCREEN_REQUIRED"
        ? "Please select entire screen only. Chrome tab or window sharing is not allowed."
        : "Screen sharing is required for this assessment. Please refresh the page and allow screen sharing when prompted.");
    }
  };

  // Function to send screen chunk to backend
  const sendScreenChunkToBackend = async () => {
    if (screenChunksRef.current.length === 0) return;
    
    try {
      const blob = new Blob(screenChunksRef.current, { type: 'video/webm' });
      const formData = new FormData();
      formData.append('screen_chunk', blob);
      formData.append('test_id', test_id);
      formData.append('chunk_index', Date.now()); // Use timestamp as chunk identifier
      
      await axios.post('/api/upload_screen_chunk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Clear sent chunks
      screenChunksRef.current = [];
    } catch (error) {
      console.error("Error sending screen chunk to backend:", error);
    }
  };

  // Function to stop screen recording
  // IMPORTANT: recorder.stop() is async — ondataavailable fires with final chunk,
  // then onstop fires. We must wait for onstop before proceeding so no chunks are lost.
  const stopScreenRecording = async () => {
    const recorder = screenRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    await new Promise((resolve) => {
      // Override onstop to flush final chunk and resolve the promise
      const originalOnStop = recorder.onstop;
      recorder.onstop = async () => {
        // Run the original onstop handler first (flushes final chunk)
        if (originalOnStop) {
          try { await originalOnStop(); } catch (e) {}
        }
        resolve();
      };

      try {
        recorder.stop();
      } catch (err) {
        console.warn("[iCode] recorder.stop() threw:", err);
        resolve();
      }
    });

    // All chunks flushed — stop all media tracks
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
    }

    screenRecorderRef.current = null;
    screenStreamRef.current = null;
    screenChunksRef.current = [];
    setIsScreenRecording(false);
    setScreenSharingActive(false);
  };
  
  const handleRun = async () => {
    isRunningRef.current = true;
    setOutput("Running code...");
    setTestResults([]);
    setTestSummary({ passed: 0, total: 0 });
    setIsSubmitting(false);

    try {
      const response = await fetch("/api/run_code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: questions[currentQuestionIndex],
          questionId: currentQuestionIndex,
          code,
          language,
          test_limit: 2
        }),
      });

      const data = await response.json();
      
      // Update execution history
      setExecutionHistory(prev => ({
        ...prev,
        runs: prev.runs + 1,
        all_tests_passed: data.results && data.results.every(r => r.passed),
        time_taken: (Date.now() - prev.start_time) / 1000
      }));

      if (data.error) {
        setTestResults([]);
        setOutput(`Error: ${data.error}`);
        speak("There was an error running your code. Please check your code and try again.");
      } else {
        const results = data.results.slice(0, 2);
        setTestResults(results);
        setOutput("Execution completed.");
        const passedCount = results.filter(result => result.passed).length;
        
        if (passedCount === results.length) {
          speak("Great job! All test cases passed.");
          if (isVeryHard) {
            setCompletedLevels(prev => ({
              ...prev,
              [currentQuestionIndex]: true
            }));
          }
        } else {
          speak("Some test cases failed. Please review your code and try again.");
        }

        const updatedResponses = [...userResponses];
        updatedResponses[currentQuestionIndex] = code;
        setUserResponses(updatedResponses);
      }
    } catch (error) {
      setOutput(`Server error: ${error.message}`);
      speak("A server error occurred. Please try again later.");
    } finally {
      isRunningRef.current = false;
    }
  };

  const handleTaskSubmit = async () => {
    console.log('[Voice] handleTaskSubmit: Task submitted, currentFlatIndex:', currentFlatIndex, 'isVeryHard:', isVeryHard);
    readyToRunRef.current = false;

    if (isVeryHard) {
      // Move to next level in current group
      if (currentLevel < 3) {
        console.log('[Voice] handleTaskSubmit: Moving to next level');
        // Follow-up gate (very-hard mode, intra-group level unlock).
        // Same pattern as the flat-index path below; wraps the legacy
        // 3 lines into a deferred callback that only fires after the
        // FollowupCallModal closes (or immediately when the flag is off).
        const _doAdvanceLevel = async () => {
          setCurrentLevel(prev => prev + 1);
          const nextQuestion = questions[currentGroup * 4 + currentLevel + 1]?.description;
          console.log('[Voice] handleTaskSubmit: Speaking level unlock:', nextQuestion?.substring(0, 50));
          await speak(`Level ${currentLevel + 2} unlocked: ${nextQuestion}`);
        };
        gateAdvanceWithFollowup(currentQuestionIndex, code, _doAdvanceLevel);
      }
      // Move to next group
      else if (currentGroup < groupQuestions.length - 1) {
        console.log('[Voice] handleTaskSubmit: Moving to next group');
        // Follow-up gate (very-hard mode, group transition).
        const _doAdvanceGroup = async () => {
          setCurrentGroup(prev => prev + 1);
          setCurrentLevel(0);
          const nextQuestion = questions[(currentGroup + 1) * 4]?.description;
          console.log('[Voice] handleTaskSubmit: Speaking next question:', nextQuestion?.substring(0, 50));
          await speak(`Code Successfully Submitted. Here is your next question. ${currentGroup + 2}: ${nextQuestion}`);
        };
        gateAdvanceWithFollowup(currentQuestionIndex, code, _doAdvanceGroup);
      }
      // Final submission
      else {
        if (isTimerExpiredRef.current) {
          console.log('[Voice] handleTaskSubmit: Timer expired (VeryHard) — auto-submitting');
          isTimerExpiredRef.current = false;
          handleFinalSubmission();
        } else {
          console.log('[Voice] handleTaskSubmit: Final submission, showing confirm modal');
          setShowConfirmModal(true);
        }
      }
    } else {
      if (currentFlatIndex < questions.length - 1) {
        console.log('[Voice] handleTaskSubmit: Moving to next question, currentFlatIndex:', currentFlatIndex);
        const updatedResponses = [...userResponses];
        updatedResponses[currentQuestionIndex] = code;
        setUserResponses(updatedResponses);

        // If timer expired, silently evaluate + save current question to session
        // Uses codeRef.current (always fresh) instead of code state (stale in closure)
        if (isTimerExpiredRef.current) {
          const currentCode = codeRef.current || code;
          if (currentCode && currentCode.trim()) {
            console.log('[AutoSave] Timer expired on Q', currentQuestionIndex, '— saving to session');
            try {
              await fetch("/api/run_code", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  question:   questions[currentQuestionIndex],
                  questionId: currentQuestionIndex,
                  code:       currentCode,
                  language,
                  test_limit: 999  // run all test cases for accurate score
                }),
              });
              console.log('[AutoSave] Q', currentQuestionIndex, 'saved successfully');
            } catch (e) {
              console.error('[AutoSave] Failed to save Q', currentQuestionIndex, e);
              // Non-blocking — continue to next question regardless
            }
          }
          isTimerExpiredRef.current = false;
        }

        // ── Follow-up gate ─────────────────────────────────────────────
        // When the super-admin enabled followup_calls_enabled, intercept
        // the "advance to next question" path so the FollowupCallModal
        // can ask 2-3 voice questions about the code that was just
        // submitted. When the flag is OFF, the helper runs the advance
        // immediately so this block behaves exactly like the legacy
        // flow. Wrap the existing 4 lines (state advance + voice cue)
        // verbatim into the deferred callback.
        const _doAdvanceFlatNext = async () => {
          setCurrentFlatIndex(prev => prev + 1);
          setTimeLeft(getQuestionTime(currentFlatIndex + 1));
          minuteAlertSpokenRef.current = false;
          const nextQuestion = questions[currentQuestionIndex + 1]?.description;
          console.log('[Voice] handleTaskSubmit: Speaking next question:', nextQuestion?.substring(0, 50));
          await speak(`Code Successfully Submitted. Here is your next question. ${nextQuestion}`);
        };
        gateAdvanceWithFollowup(currentQuestionIndex, code, _doAdvanceFlatNext);
      } else {
        // If timer expired → auto-submit immediately without confirm modal
        if (isTimerExpiredRef.current) {
          console.log('[Voice] handleTaskSubmit: Timer expired on last question — auto-submitting');
          isTimerExpiredRef.current = false;
          handleFinalSubmission();
        } else {
          console.log('[Voice] handleTaskSubmit: Last question, showing confirm modal');
          setShowConfirmModal(true);
        }
      }
    }

    readyToRunRef.current = true;
    console.log('[Voice] handleTaskSubmit: Task submit complete, readyToRun set to true');
  };

  // Deliberate final submission (confirm modal / manual "Submit test"): gate the
  // LAST question through the follow-up too, so EVERY question is covered — not
  // just the between-question advances. Timer-expired auto-submits call
  // handleFinalSubmission directly and stay unblocked (no time for a voice
  // check). When the flag is off or the last answer was boilerplate-only,
  // gateAdvanceWithFollowup runs handleFinalSubmission immediately.
  const handleConfirmedFinalSubmit = () => {
    setShowConfirmModal(false);
    gateAdvanceWithFollowup(currentQuestionIndex, codeRef.current || "", handleFinalSubmission);
  };

  const handleFinalSubmission = async () => {
    // Show loading overlay immediately and close confirm modal
    setIsSubmitting(true);
    setShowConfirmModal(false);

    interviewActiveRef.current = false;

    await stopScreenRecording();

    // Stop desktop proctoring and submit data
    if (isDesktopApp()) {
      console.log("[Desktop] Final submission - stopping proctoring...");
      try {
        const proctoringResult = await stopDesktopProctoring();
        console.log("[Desktop] Proctoring stopped:", proctoringResult);
        await submitProctoringData(test_id, testType);
        console.log("[Desktop] Proctoring data submitted");

        // Upload screen recording to S3
        console.log("[Desktop] Uploading screen recording...");
        const uploadResult = await uploadRecording(test_id);
        console.log("[Desktop] Recording upload result:", uploadResult);
      } catch (error) {
        console.error("[Desktop] Error stopping proctoring:", error);
      }
    }

    const updatedResponses = [...userResponses];
    updatedResponses[currentQuestionIndex] = code;
    setUserResponses(updatedResponses);

    const submitPayload = JSON.stringify({
      test_id: test_id,
      test_type: testType,
      questions,
      user_responses: updatedResponses,
      language,
      difficulty: isVeryHard ? "Very Hard" : "Medium",
      violations: {
        pasteAttempts,
        specialKeyAttempts,
        rightClickAttempts,
        switchCount,
        fullscreenExitCount
      },
      typing_metrics: {
        avgSpeed: typingMetrics.avgSpeed,
        suspiciousBursts: typingMetrics.suspiciousBursts,
        longPauses: typingMetrics.pauses.filter(p => p > 30000).length,
        totalKeystrokes: typingMetrics.totalChars
      },
      execution_history: {
        runs: executionHistory.runs,
        all_tests_passed: executionHistory.all_tests_passed,
        time_taken: executionHistory.time_taken
      }
    });

    // Fire submit & recording in background — keepalive ensures request completes
    // even after page navigates. Show popup immediately without waiting.
    fetch("/api/submit_code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: submitPayload,
      keepalive: true,   // survives page unload/navigation
    }).catch(err => console.error("[Submit] submit_code failed:", err));

    fetch('/api/merge_screen_recording', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ test_id }),
      keepalive: true,
    }).catch(() => {});

    // Show success popup immediately — don't block on API response
    setIsSubmitting(false);
    setShowPopup(true);

    await speak("Code successfully submitted. Thank you for completing the coding test!");

    setTimeout(() => {
      window.location.href = "/user-home";
    }, 3000);
  };

  const getLanguageExtension = () => {
    // normalize language casing for safety
    const lang = (language || "Python").toLowerCase();
    switch (lang) {
      case "python":
        return python();
      case "javascript":
      case "nodejs":
        return javascript();
      // C and C++ share CodeMirror's cpp() mode — there isn't a separate
      // pure-C mode shipped in @codemirror/lang-cpp, and the syntax
      // overlap is close enough that recruiters won't notice (printf,
      // braces, semicolons, types all highlight identically). The
      // BACKEND still receives "C" verbatim as the language string, so
      // boilerplate / plagiarism / scoring all behave correctly — this
      // is purely an editor-highlighting fallback.
      case "c++":
      case "cpp":
      case "c":
        return cpp();
      case "java":
        return java();
      default:
        return python();
    }
  };

  // Format timer as MM:SS.
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleSubmitTest = async () => {
    interviewActiveRef.current = false;

    await stopScreenRecording();

    // Stop desktop proctoring and submit data
    if (isDesktopApp()) {
      console.log("[Desktop] Stopping proctoring and submitting data...");
      try {
        const proctoringResult = await stopDesktopProctoring();
        console.log("[Desktop] Proctoring stopped:", proctoringResult);
        await submitProctoringData(test_id, testType);
        console.log("[Desktop] Proctoring data submitted");

        // Upload screen recording to S3
        console.log("[Desktop] Uploading screen recording...");
        const uploadResult = await uploadRecording(test_id);
        console.log("[Desktop] Recording upload result:", uploadResult);
      } catch (error) {
        console.error("[Desktop] Error stopping proctoring:", error);
      }
    }

    const updatedResponses = [...userResponses];
    updatedResponses[currentQuestionIndex] = code;
    setUserResponses(updatedResponses);

    fetch("/api/submit_code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        test_id: test_id,
        questions,
        user_responses: updatedResponses,
        language,
        difficulty: isVeryHard ? "Very Hard" : "Medium",
        violations: {
          pasteAttempts,
          specialKeyAttempts,
          rightClickAttempts,
          switchCount,
          fullscreenExitCount
        },
        typing_metrics: {
          avgSpeed: typingMetrics.avgSpeed,
          suspiciousBursts: typingMetrics.suspiciousBursts,
          longPauses: typingMetrics.pauses.filter(p => p > 30000).length,
          totalKeystrokes: typingMetrics.totalChars
        },
        execution_history: {
          runs: executionHistory.runs,
          all_tests_passed: executionHistory.all_tests_passed,
          time_taken: executionHistory.time_taken
        }
      }),
    });

    fetch('/api/merge_screen_recording', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ test_id })
    });

    setShowPopup(true);
    await speak("Test submitted. Thank you for completing the coding test!");

    navigate("/user-home", {
      state: {
        showTestSubmittedPopup: true
      }
    });
  };

  // Handler for when user acknowledges start modal
  const handleAcknowledgeAndStart = () => {
    console.log('[Voice] handleAcknowledgeAndStart: User acknowledged start modal');
    // Close modal and initialize everything
    setShowStartModal(false);

    // initializeEverything will be invoked by the useEffect watching showStartModal
  };

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Layer 0 Identity Gate. Renders as a full-screen modal overlay
          when verification is required. Calls onPass() either immediately
          (no identity sub-features enabled) or after the candidate
          completes ID upload + selfie + face match. Mounted at the top of
          the JSX so it sits above everything including the start modal. */}
      {!identityVerified && (
        <IdentityGate
          testId={test_id}
          onPass={() => setIdentityVerified(true)}
          // For high-volume hiring it's better to let borderline-similar
          // candidates through with a recruiter note than to block them
          // outright at this gate. Recruiter sees the verdict in the
          // cheating dashboard either way.
          allowManualReviewContinue={true}
        />
      )}

      {/* Layer 1 Environment Baseline. Same modal-overlay pattern as the
          identity gate. Only mounts AFTER identity has passed — running
          both at once would stack two full-screen modals confusingly.
          Auto-passes when no Layer 1 features are enabled in super-admin. */}
      {identityVerified && !environmentBaselineDone && (
        <EnvironmentBaselineGate
          testId={test_id}
          onPass={() => setEnvironmentBaselineDone(true)}
        />
      )}

      {/* Layer 3 proctoring engine. Renders only an offscreen <video> element
          and a fixed-position warning overlay — no layout impact on the test
          UI. Each detector inside is gated by its own super-admin flag, so
          this mount is safe to leave in place even when no detectors are
          enabled (the engine no-ops). */}
      <ProctoringEngine
        stream={streamRef.current}
        testId={test_id}
        active={interviewActiveRef.current !== false}
      />

      {/* Layer 2 lockdown engine — the THREE new detectors only (dev-tools,
          virtual camera, multi-monitor). Existing paste / right-click /
          fullscreen / tab-switch detectors continue running from their
          original useEffect hooks in this file. Engine renders nothing. */}
      <LockdownEngine
        testId={test_id}
        active={interviewActiveRef.current !== false}
      />

      {/* Layer 4 — Rekognition Snapshots. Periodic capture every N seconds
          (admin-tunable) plus event-triggered capture whenever Layer 3 /
          Lockdown detectors fire. Compares against the Layer 1 baseline
          catalog server-side. Renders only an offscreen <video>. */}
      <SnapshotEngine
        stream={streamRef.current}
        testId={test_id}
        active={interviewActiveRef.current !== false}
      />

      {/* Test Panel - Floating debug panel to trigger all UI elements (Dev Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 z-[9999]">
          <Button
            onClick={() => setShowTestPanel(!showTestPanel)}
            variant="outline"
            size="sm"
            className="bg-background/90 backdrop-blur-sm shadow-lg"
          >
            <Bug className="w-4 h-4 mr-2" />
            Test UI
            {showTestPanel ? <ChevronDown className="w-4 h-4 ml-2" /> : <ChevronUp className="w-4 h-4 ml-2" />}
          </Button>
          
          {showTestPanel && (
          <Card className="mt-2 w-80 max-h-[80vh] overflow-y-auto bg-background/95 backdrop-blur-sm shadow-xl border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Test Floating UI Elements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={() => setShowStartModal(true)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Start Modal
              </Button>
              
              <Button
                onClick={() => setShowSpecialKeyWarning(true)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                System Keys Warning
              </Button>
              
              <Button
                onClick={() => {
                  setShowRightClickWarning(true);
                  setTimeout(() => setShowRightClickWarning(false), 3000);
                }}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Right-Click Warning
              </Button>
              
              <Button
                onClick={() => {
                  setShowPasteWarning(true);
                  setTimeout(() => setShowPasteWarning(false), 3000);
                }}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Copy-Paste Warning
              </Button>
              
              <Button
                onClick={() => {
                  setShowTabSwitchWarning(true);
                  setTimeout(() => setShowTabSwitchWarning(false), 3000);
                }}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Tab Switch Warning
              </Button>
              
              <Button
                onClick={() => setShowFullscreenWarning(true)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Fullscreen Exit Warning
              </Button>
              
              <Button
                onClick={() => setIsSubmitting(true)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Submitting Overlay
              </Button>
              
              <Button
                onClick={() => setShowPopup(true)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Success Popup
              </Button>
              
              <Button
                onClick={() => setShowConfirmModal(true)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                Confirm Modal
              </Button>
              
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Screen Sharing Dialog appears when screen sharing is inactive</p>
                <Button
                  onClick={() => {
                    setScreenSharingActive(false);
                    setIsScreenRecording(false);
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                >
                  Trigger Screen Sharing Dialog
                </Button>
              </div>
              
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Desktop Violation Warning requires desktop app</p>
                <p className="text-xs text-muted-foreground mb-2">Language Note appears when NodeJS is selected</p>
                <Button
                  onClick={() => {
                    // Mock success results
                    const mockResults = [
                      {
                        passed: true,
                        input: "[3,2,3]",
                        expected: "3",
                        output: "3"
                      },
                      {
                        passed: true,
                        input: "[2,2,1,1,1,2,2]",
                        expected: "2",
                        output: "2"
                      },
                      {
                        passed: true,
                        input: "[1]",
                        expected: "1",
                        output: "1"
                      },
                      {
                        passed: true,
                        input: "[1,1,2]",
                        expected: "1",
                        output: "1"
                      },
                      {
                        passed: true,
                        input: "[6,5,5]",
                        expected: "5",
                        output: "5"
                      }
                    ];
                    setTestResults(mockResults);
                    setTestSummary({ passed: 5, total: 5 });
                    setOutput("Execution completed.");
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                >
                  Add Mock Success Results
                </Button>
                
                <Button
                  onClick={() => {
                    const mockFailureResults = [
                      {
                        passed: true,
                        input: "[3,2,3]",
                        expected: "3",
                        output: "3"
                      },
                      {
                        passed: true,
                        input: "[2,2,1,1,1,2,2]",
                        expected: "2",
                        output: "2"
                      },
                      {
                        passed: false,
                        input: "[1]",
                        expected: "1",
                        output: "0",
                        error: "Incorrect result. Expected 1 but got 0."
                      },
                      {
                        passed: false,
                        input: "[1,1,2]",
                        expected: "1",
                        output: "2",
                        error: "Wrong majority element detected."
                      },
                      {
                        passed: false,
                        input: "[6,5,5]",
                        expected: "5",
                        output: null,
                        error: "TypeError: Cannot read property 'length' of undefined"
                      }
                    ];
                    setTestResults(mockFailureResults);
                    setTestSummary({ passed: 2, total: 5 });
                    setOutput("Execution completed.");
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                >
                  Add Mock Failure Results
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      )}
      
      {/* START MODAL: opens only AFTER BOTH pre-test gates have cleared.
          Order is enforced by the gate components themselves — identity
          must pass before environment baseline mounts, and baseline must
          pass before this modal renders. */}
      <Dialog open={showStartModal && identityVerified && environmentBaselineDone} onOpenChange={() => {}}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Assessment Instructions</DialogTitle>
            <DialogDescription className="text-base mt-2">
              Please read the following rules and guidelines carefully before starting your assessment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-semibold text-primary">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Multiple Questions</p>
                  <p className="text-sm text-muted-foreground">
                    This assessment contains <strong>4 questions</strong>. You must complete them one at a time in the order presented.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-semibold text-primary">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">No Going Back</p>
                  <p className="text-sm text-muted-foreground">
                    Once you submit a question, you <strong>cannot return</strong> to it. Make sure you're satisfied with your solution before submitting.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-semibold text-primary">3</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Time Limit</p>
                  <p className="text-sm text-muted-foreground">
                    Each question has a <strong>time limit</strong>. Monitor the timer in the top-right corner and manage your time accordingly.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-error/10 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-semibold text-error">4</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">No Copy-Paste</p>
                  <p className="text-sm text-muted-foreground">
                    Copy-paste functionality is <strong>disabled</strong>. You must write all code yourself. Multiple attempts to copy-paste will result in automatic test termination.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-error/10 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-semibold text-error">5</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Restricted Actions</p>
                  <p className="text-sm text-muted-foreground">
                    System keys (Alt, Windows, Function keys), right-click, and tab switching are <strong>monitored and restricted</strong>. Repeated violations will result in test termination.
                  </p>
                </div>
              </div>
            </div>

            <Alert className="mt-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Proctoring & Recording Consent</AlertTitle>
              <AlertDescription className="mt-2">
                By clicking "Start Assessment", you consent to screen and audio recording for proctoring purposes. 
                Your session will be monitored to ensure assessment integrity. All recordings are handled in accordance with our privacy policy.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter className="mt-6">
            <Button
              onClick={handleAcknowledgeAndStart}
              className="w-full sm:w-auto"
              size="lg"
            >
              Start Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Desktop violation warning - shows alerts from desktop proctoring app */}
      <DesktopViolationWarning enabled={testStarted} />

      {/* Screen sharing status indicator - only show if not already recording */}
      {/* Warnings now use Sonner toasts - see useEffect hooks below */}

      {!screenSharingActive && !isScreenRecording && (
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive font-bold">Screen Share</DialogTitle>
              <DialogDescription>
                <p className="mt-4 text-destructive font-bold">
                  !!Please select Entire Screen Option with audio while sharing screen!!
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={initializeScreenRecording}
                disabled={isButtonDisabled}
                className="w-full sm:w-auto"
              >
                Start Screen Sharing
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/user-home")}
                className="w-full sm:w-auto text-destructive border-destructive font-bold hover:bg-destructive/10"
              >
                Cancel Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Fullscreen exit warning */}
      <AlertDialog open={showFullscreenWarning} onOpenChange={setShowFullscreenWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-warning">Warning: Fullscreen Exited</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mt-2">Please return to fullscreen mode to continue your test.</p>
              <p className="mt-2">Your test will be automatically submitted in: <strong>{exitTimer} seconds</strong></p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {exitTimer > 1 && (
              <AlertDialogAction onClick={handleReturnToFullscreen}>
                Return to Fullscreen
              </AlertDialogAction>
            )}
            <Button onClick={handleSubmitTest} variant="destructive" className="mt-2 sm:mt-0">
              Submit Test Now
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tab/window switch warning - Using Sonner toast (see useEffect below) */}
      
      {/* Top Header Section - Modern Design */}
      <div className="px-6 py-3 backdrop-blur-sm border-b border-border min-h-[56px] flex-shrink-0 bg-card/50">
        <div className="flex items-center justify-between w-full">
          {/* Left: Navigation */}
          <div className="flex items-center gap-4">
            <div className="text-lg font-bold text-primary mr-6 uppercase tracking-wide">AI Evaluator</div>
            <div className="h-6 w-px bg-border" />
            <div className="flex gap-2 items-center">
              {isVeryHard ? (
                groupQuestions.map((_, groupIndex) => (
                  <Button
                    key={groupIndex}
                    variant={currentGroup === groupIndex ? "default" : "ghost"}
                    size="sm"
                    className="h-8"
                    onClick={() => {
                      const updatedResponses = [...userResponses];
                      updatedResponses[currentQuestionIndex] = code;
                      setUserResponses(updatedResponses);
                      setCurrentGroup(groupIndex);
                      setCurrentLevel(0);
                    }}
                    disabled
                  >
                    Task {groupIndex + 1}
                  </Button>
                ))
              ) : (
                questions.map((_, index) => (
                  <Button
                    key={index}
                    variant={currentQuestionIndex === index ? "default" : "ghost"}
                    size="sm"
                    className="h-8"
                    onClick={() => {
                      const updatedResponses = [...userResponses];
                      updatedResponses[currentQuestionIndex] = code;
                      setUserResponses(updatedResponses);
                      setCurrentFlatIndex(index);
                    }}
                    disabled
                  >
                    Task {index + 1}
                  </Button>
                ))
              )}
            </div>
            
            {/* Level progress for progressive mode */}
            {isVeryHard && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  {[0, 1, 2, 3].map(level => (
                    <Badge
                      key={level}
                      variant={
                        level === currentLevel ? "default" : 
                        level < currentLevel ? "success" : "outline"
                      }
                      className="px-2.5 py-0.5 text-xs"
                    >
                      {["Medium", "Hard", "V. Hard", "Extreme"][level]}
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right: Timer and Submit */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border">
              <Clock className="w-4 h-4 text-foreground" />
              <span className="font-mono font-semibold text-sm tabular-nums">
                {formatTime(timeLeft)}
              </span>
            </div>
            <Button 
              onClick={handleTaskSubmit}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm h-8 font-bold"
            >
              {currentQuestionIndex === 3 ? "Final Submit" : "Submit Task"}
            </Button>
          </div>
        </div>
      </div>

      {/* Copy-paste warning - Using Sonner toast (see useEffect below) */}

      {/* Voice-fallback banner: shown when the audio intro could not complete
          within the hard ceiling. The Run button + timer are already enabled
          at this point — this banner exists only to explain WHY there was no
          audio, so the candidate doesn't think the page is broken. */}
      {voiceFallbackActive && (
        <div className="px-4 py-2 bg-warning/10 border-y border-warning/30">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
            <span>
              Audio narration is unavailable right now. Please read the
              instructions on the left — your test is fully functional and
              the timer is running.
            </span>
            <button
              onClick={() => setVoiceFallbackActive(false)}
              className="ml-auto text-xs underline opacity-70 hover:opacity-100"
              aria-label="Dismiss audio warning"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content Section - Modern Two-Column Layout with Resizable Split */}
      <div className="flex flex-1 bg-background relative overflow-hidden min-h-0" ref={resizeRef}>
        {/* Left Column - Problem Description (Resizable) */}
        <div 
          className="h-full flex flex-col border-r bg-card overflow-hidden"
          style={{ 
            width: `${leftPanelWidth}%`, 
            minWidth: '300px', 
            maxWidth: '60%'
          }}
        >
          {/* Problem Content - Scrollable */}
          <div
            className="problem-panel-scrollable flex-1 overflow-y-scroll overflow-x-hidden p-6 space-y-6 min-h-0"
          >
            {/* Problem Header */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <h1 className="text-2xl font-bold text-foreground leading-tight">
                  {questions[currentQuestionIndex]?.title || "Problem Title"}
                </h1>
                {isVeryHard && (
                  <Badge variant="outline" className="shrink-0">
                    {["Medium", "Hard", "Very Hard", "Extreme"][currentLevel]}
                  </Badge>
                )}
              </div>
              
              {/* Problem Description */}
              <div className="prose prose-sm max-w-none text-foreground">
                <div className="text-base leading-relaxed text-muted-foreground whitespace-pre-wrap break-words mb-4"
                  style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {questions[currentQuestionIndex]?.description || "Problem description goes here..."}
                </div>
              </div>
            </div>

            {/* Test Cases Section */}
            {!isVeryHard && questions[currentQuestionIndex]?.test_cases && questions[currentQuestionIndex]?.test_cases.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <h2 className="text-lg font-semibold text-foreground">Example Test Cases</h2>
                <div className="space-y-3">
                  {questions[currentQuestionIndex]?.test_cases.slice(0, 2).map((test, index) => (
                    <Card key={index} className="p-4 bg-muted/30 border-border">
                      <div className="space-y-2 font-mono text-sm">
                        <div>
                          <span className="text-primary font-semibold">Input:</span>
                          <div className="mt-1 p-2 bg-background rounded border border-border text-foreground">
                            {JSON.stringify(test.input, null, 2)}
                          </div>
                        </div>
                        <div>
                          <span className="text-primary font-semibold">Expected Output:</span>
                          <div className="mt-1 p-2 bg-background rounded border border-border text-foreground">
                            {JSON.stringify(test.expected, null, 2)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Language-specific Note */}
            {language === "NodeJS" && (
              <Card className="bg-warning/10 border-warning/30 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground leading-relaxed">
                    <strong>Note:</strong> You should return <code className="bg-muted px-1.5 py-0.5 rounded text-error font-mono text-xs">"true"</code> instead of <code className="bg-muted px-1.5 py-0.5 rounded text-error font-mono text-xs">true</code> in your solution.
                  </p>
                </div>
              </Card>
            )}
          </div>

        </div>

        {/* Resize Handle */}
        <div
          className="w-1 min-w-[4px] bg-border hover:bg-primary cursor-col-resize flex items-center justify-center transition-colors relative z-10 group"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
        >
          <GripVertical className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Middle Column - Editor & Console (fills remaining space) */}
        <div
          className="bg-background flex flex-col h-full min-h-0 flex-1"
          style={{ minWidth: '40%' }}
        >
          {/* Editor Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0 bg-card/50">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-[160px] h-9 bg-background border-border text-foreground hover:bg-accent">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="dark bg-popover border-border text-popover-foreground">
                    <SelectItem value="Python" className="cursor-pointer focus:bg-accent focus:text-accent-foreground">Python</SelectItem>
                    <SelectItem value="JavaScript" className="cursor-pointer focus:bg-accent focus:text-accent-foreground">JavaScript</SelectItem>
                    <SelectItem value="C++" className="cursor-pointer focus:bg-accent focus:text-accent-foreground">C++</SelectItem>
                    {/* "C" added next to C++ — the candidate sees both
                        systems languages back-to-back in the picker. */}
                    <SelectItem value="C" className="cursor-pointer focus:bg-accent focus:text-accent-foreground">C</SelectItem>
                    <SelectItem value="Java" className="cursor-pointer focus:bg-accent focus:text-accent-foreground">Java</SelectItem>
                    <SelectItem value="NodeJS" className="cursor-pointer focus:bg-accent focus:text-accent-foreground">NodeJS</SelectItem>
                  </SelectContent>
                </Select>

                {/* Theme Toggle Button */}
                <Button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  variant="outline"
                  size="sm"
                  className="h-9 w-9 p-0 border-border hover:bg-accent"
                  title={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
                >
                  {isDarkMode ? (
                    <Sun className="w-4 h-4 text-foreground" />
                  ) : (
                    <Moon className="w-4 h-4 text-foreground" />
                  )}
                </Button>
              </div>

              <Button 
                onClick={handleRun}
                disabled={!readyToRunRef.current || isRunningRef.current}
                size="sm"
                className="bg-success text-success-foreground hover:bg-success/90 shadow-sm h-9"
              >
                {isRunningRef.current ? (
                  "Running..."
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-1.5" />
                    Run Code
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Editor and Console Container */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Code Editor - Takes 60% of available space */}
            <div 
              className="bg-background relative flex-[0_0_60%] min-h-0 select-none"
              ref={editorRef}
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
            >
              <CodeMirror
                value={code}
                height="100%"
                theme={isDarkMode ? abcdef : githubLight}
                extensions={[getLanguageExtension()]}
                onChange={(value) => { setCode(value); codeRef.current = value; }}
                className="code-mirror-icode"
                basic={{
                  lineNumbers: true,
                  foldGutter: false,
                  highlightActiveLine: true,
                  highlightSelectionMatches: false,
                  lint: false
                }}
                options={{
                  lineNumbers: true,
                  mode: language,
                  indentUnit: 4,
                  smartIndent: true,
                  lineWrapping: false,
                  matchBrackets: true,
                  autoCloseBrackets: true,
                  styleActiveLine: true
                }}
              />
            </div>

            {/* Console/Test Results - Takes 40% of available space */}
            <div 
              className="overflow-hidden flex flex-col flex-[0_0_40%] min-h-0 border-t bg-card"
              style={{ maxHeight: '100%' }}
            >
              <div className="px-4 py-3 border-b bg-muted/30 flex-shrink-0">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Test Results</h3>
              </div>
              <div 
                className="flex-1 overflow-y-auto min-h-0 console-panel-scrollable"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'hsl(var(--muted)) hsl(var(--muted) / 0.1)',
                  scrollbarGutter: 'stable',
                  maxHeight: '100%'
                }}
              >
                <div className="px-4 pt-6 pb-6 space-y-3">
                {output && (
                  <div className={cn(
                    "text-sm font-medium p-3 rounded-md",
                    testResults.length === 0 && output !== "Running code..." ? "bg-error/10 text-error border border-error/20" : 
                    output === "Execution completed." && testResults.length > 0 ? (
                      testResults.every(test => test.passed) 
                        ? "bg-success/10 text-success border border-success/20" 
                        : "bg-error/10 text-error border border-error/20"
                    ) :
                    "bg-muted/50 text-foreground"
                  )}>
                    <div className="flex items-center gap-3">
                      <span>{output}</span>
                      {testResults.length > 0 && output !== "Running code..." && (
                        <span className="text-xs font-semibold opacity-90 whitespace-nowrap">
                         <span className= "text-success"> Passed: </span><span className= "text-foreground">{testResults.filter(t => t.passed).length}</span> / <span className= "text-error"> Failed: </span><span className="text-foreground">{testResults.filter(t => !t.passed).length}</span>
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {testResults.length > 0 && (
                  <div className="space-y-3">
                  {testResults.map((test, index) => (
                    <Card 
                      key={index} 
                      className={cn(
                        "p-4 border-2",
                        test.passed ? "bg-success/5 border-success/30" : "bg-error/5 border-error/30"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Badge 
                          variant={test.passed ? "success" : "error"}
                          className="text-xs"
                        >
                          Test Case {index + 1}
                        </Badge>
                        <span className={cn(
                          "text-sm font-semibold flex items-center gap-1",
                          test.passed ? "text-success" : "text-error"
                        )}>
                          {test.passed ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              <span>Passed</span>
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4" />
                              <span>Failed</span>
                            </>
                          )}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-xs font-mono">
                        <div>
                          <span className="text-muted-foreground font-sans">Input: </span>
                          <code className="text-foreground bg-muted px-2 py-1 rounded">
                            {JSON.stringify(test.input)}
                          </code>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-sans">Expected: </span>
                          <code className="text-foreground bg-muted px-2 py-1 rounded">
                            {JSON.stringify(test.expected)}
                          </code>
                        </div>
                        {test.error ? (
                          <div>
                            <span className="text-error font-sans">Error: </span>
                            <code className="block mt-1 p-2 bg-error/10 border border-error/20 rounded text-error">
                              {test.error}
                            </code>
                          </div>
                        ) : (
                          <div>
                            <span className="text-muted-foreground font-sans">Output: </span>
                            <code className="text-foreground bg-muted px-2 py-1 rounded">
                              {JSON.stringify(test.output)}
                            </code>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
              
              {!output && testResults.length === 0 && (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  Run your code to see test results here
                </div>
              )}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Right Column - Proctoring: live camera + voice visualizer.
            Fixed-width third column so the candidate always sees the system
            watching + listening. Both tiles share the exact same size. */}
        <div className="flex-shrink-0 h-full border-l border-border bg-card flex flex-col overflow-hidden" style={{ width: '260px' }}>
          <div className="px-4 py-3 border-b border-border flex-shrink-0 bg-card/50">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Proctoring</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
            {/* Camera tile */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Camera</span>
              <div
                className="rounded-lg overflow-hidden bg-background transition-all duration-300"
                style={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  border: '2px solid var(--info)',
                  boxShadow: '0 0 0 2px color-mix(in oklch, var(--info) 22%, transparent)',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.warn("Video element error (non-critical):", e);
                  }}
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.play().catch(() => {
                        // Autoplay blocked - will try on user interaction
                      });
                    }
                  }}
                ></video>
              </div>
            </div>

            {/* Visualizer tile — same size as the camera tile */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Voice</span>
              <div
                className="rounded-lg overflow-hidden bg-background flex items-center justify-center transition-all duration-300"
                style={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  border: `2px solid ${isSpeaking ? 'var(--success)' : 'var(--border)'}`,
                  boxShadow: isSpeaking
                    ? '0 0 0 2px color-mix(in oklch, var(--success) 25%, transparent)'
                    : 'none',
                }}
              >
                <div
                  className="ivisualizer-header"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EnergyVisualizer isSpeaking={isSpeaking} isRecording={!isSpeaking} />
                </div>
              </div>
            </div>

            {/* Status badge */}
            <div className="flex justify-center">
              <Badge
                variant={isSpeaking ? "success" : "info"}
                className="text-xs py-1 px-3 flex items-center gap-1.5"
              >
                {isSpeaking ? (
                  <>
                    <Mic className="w-3.5 h-3.5" />
                    <span className="text-xs">Speaking</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5" />
                    <span className="text-xs">Observing</span>
                  </>
                )}
              </Badge>
            </div>
          </div>
        </div>
      </div>


      {/* Submitting overlay - blocks user interaction during upload */}
      <Dialog open={isSubmitting} onOpenChange={() => {}}>
        <DialogContent>
          <DialogHeader>
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 border-4 border-muted border-t-success rounded-full animate-spin mb-4" />
              <DialogTitle>Submitting Your Code...</DialogTitle>
              <DialogDescription className="mt-2">
                Please wait while we upload your screen recording and submit your test.
              </DialogDescription>
              <p className="text-xs text-muted-foreground mt-2">
                Do not close this window.
              </p>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* Custom Popup */}
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent>
          <DialogHeader>
            <div className="flex flex-col items-center text-center">
              <FaCheckCircle className="text-success mb-4" size={50} />
              <DialogTitle>Code Successfully Submitted!</DialogTitle>
              <DialogDescription className="mt-2">
                Your code has been submitted.
              </DialogDescription>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => window.location.href = '/user-home'}
              className="w-full"
            >
              Go to Homepage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showConfirmModal && (
        <Modal
          isOpen={true}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmedFinalSubmit}
        />
      )}
      
      {/* Sonner Toaster for toast notifications */}
      <Toaster position="top-center" richColors />

      {/* Post-coding-question voice follow-up modal.
          Mounted as a sibling of the main editor so it sits ABOVE every
          other proctoring overlay (z-index 10000 on the modal). Closed
          and reset before the next question is visible. */}
      {followupModalOpen && followupContext && (
        <FollowupCallModal
          open={followupModalOpen}
          codingQuestion={followupContext.questionText}
          candidateCode={followupContext.code}
          language={followupContext.language}
          testId={followupContext.testId}
          questionIdx={followupContext.questionIdx}
          totalQuestions={followupContext.totalQuestions}
          // Threaded into the backend so the server-side attempt_detector
          // gate can run as defense-in-depth — even if a future code
          // path skips the client prefilter, the backend will still
          // refuse to mint a Realtime session for a boilerplate-only
          // submission.
          originalBoilerplate={followupContext.originalBoilerplate}
          candidateFirstName={
            (location?.state?.candidateName || "").trim().split(/\s+/)[0] ||
            (sessionStorage.getItem("candidate_name") || "").trim().split(/\s+/)[0] ||
            ""
          }
          onComplete={async (_result) => {
            // Close + clear FIRST so the modal doesn't briefly show
            // stale data on the next question.
            const advance = followupAdvanceRef.current;
            followupAdvanceRef.current = null;
            setFollowupModalOpen(false);
            setFollowupContext(null);
            // Then run the deferred "advance" callback that was stashed
            // when the modal opened. This is what actually navigates to
            // the next coding question.
            if (typeof advance === "function") {
              try { await advance(); }
              catch (e) { console.error("[followup] advance callback threw:", e); }
            }
          }}
        />
      )}
    </div>
  );
};

export default CodeEditor;
